import React, { useState, useEffect } from 'react';
import { Modal, Upload, Form, Select, Input, Button, Space, message } from 'antd';
import uploadedIcon from 'assets/images/image_upload_icon.svg';
import loadingUnscreenIcon from 'assets/images/loading-unscreen.gif';
import { uploadImageAndSubmit, updateImage, approveImage } from 'api/image';
import './addImageModal.css';

const { Option } = Select;

const issueTypeOptions = [
	{ value: 3, label: '胶量过少' },
	{ value: 2, label: '虚压' },
	{ value: 1, label: '持粘力不足' },
	{ value: 4, label: '喷胶位置异常' },
	{ value: 5, label: '胶量过少 & 虚压' },
];

const issueScenarioOptions = [
	{ value: 0, label: '生产线上' },
	{ value: 1, label: '仓库' },
	{ value: 2, label: '货架 或 物流中' },
	{ value: 3, label: '其他' },
];

const issuePositionOptions = [
	{ value: '顶面', label: '顶面' },
	{ value: '侧面', label: '侧面' },
];

const AddImageModal = ({
	visible,
	onCancel,
	onUploadStatusChange,
	mode = 'add',
	editPreviewImage,
	editImageName,
	editRecordId,
	editFormValues,
	appId,
	editOssKey,
}) => {
	const [form] = Form.useForm();
	const [selectedIssueTypes, setSelectedIssueTypes] = useState([]);
	const [hasUploadedFile, setHasUploadedFile] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [previewUrl, setPreviewUrl] = useState(null);
	const [fileName, setFileName] = useState('');
	const [fileUploadError, setFileUploadError] = useState('');
	const [originalFormValues, setOriginalFormValues] = useState({});
	const [hasFormChanged, setHasFormChanged] = useState(false);
	useEffect(() => {
		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [previewUrl]);

	useEffect(() => {
		if (!visible) {
			clearAllData();
		}
	}, [visible]);

	useEffect(() => {
		if (visible && (mode === 'edit' || mode === 'approve') && editPreviewImage) {
			setPreviewUrl(editPreviewImage);
			setFileName(editImageName || '');
			setHasUploadedFile(true);
		}
	}, [visible, mode, editPreviewImage, editImageName]);

	useEffect(() => {
		if (
			visible &&
			(mode === 'edit' || mode === 'approve') &&
			editFormValues &&
			Object.keys(editFormValues).length > 0
		) {
			form.setFieldsValue({
				issueType: editFormValues.issueTypes || [],
				scene: editFormValues.scene,
				position: editFormValues.position,
				issueReason: editFormValues.issueReason || '',
			});
			setSelectedIssueTypes(editFormValues.issueTypes || []);

			const originalValues = {
				issueType: editFormValues.issueTypes,
				scene: editFormValues.scene,
				position: editFormValues.position,
				issueReason: editFormValues.issueReason || '',
			};
			setOriginalFormValues(originalValues);
			setHasFormChanged(false);
		}
	}, [visible, mode, editFormValues, form]);

	const clearAllData = () => {
		if (previewUrl && !editPreviewImage) {
			URL.revokeObjectURL(previewUrl);
		}
		form.resetFields();
		setSelectedIssueTypes([]);

		setHasUploadedFile(false);
		setSelectedFile(null);
		setPreviewUrl(null);
		setFileName('');
		setFileUploadError('');

		setOriginalFormValues({});
		setHasFormChanged(false);
	};

	const handleIssueTypeChange = (value) => {
		setSelectedIssueTypes(value);
		if (mode === 'edit') {
			const currentValues = form.getFieldsValue();
			const hasChanges = checkFormChanges(currentValues);
			setHasFormChanged(hasChanges);
		}
	};

	useEffect(() => {
		if (mode === 'edit' && visible && originalFormValues && Object.keys(originalFormValues).length > 0) {
			const currentValues = form.getFieldsValue();
			const hasChanges = checkFormChanges(currentValues);
			setHasFormChanged(hasChanges);
		}
	}, [mode, visible, selectedIssueTypes, selectedFile, originalFormValues]);

	const checkFormChanges = (currentValues) => {
		if (mode !== 'edit' || !originalFormValues || Object.keys(originalFormValues).length === 0) {
			return false;
		}
		const hasIssueTypeChange = currentValues.issueType !== originalFormValues.issueType;
		const hasSceneChange = currentValues.scene !== originalFormValues.scene;
		const hasGluePositionChange = currentValues.position !== originalFormValues.position;
		const hasIssueReasonChange = currentValues.issueReason !== originalFormValues.issueReason;
		const hasFileChange = selectedFile !== null;

		return hasIssueTypeChange || hasSceneChange || hasGluePositionChange || hasIssueReasonChange || hasFileChange;
	};

	useEffect(() => {
		if (mode === 'edit' && visible && originalFormValues && Object.keys(originalFormValues).length > 0) {
			const currentValues = form.getFieldsValue();
			const hasChanges = checkFormChanges(currentValues);
			setHasFormChanged(hasChanges);
		}
	}, [selectedIssueTypes, selectedFile, form, mode, visible, originalFormValues]);

	const formatFileSize = (bytes) => {
		if (bytes === 0) return '0 Bytes';
		const decimalMB = bytes / (1000 * 1000);
		return `${decimalMB.toFixed(1)} MB`;
	};
	const handleFileSelect = (file) => {
		if (previewUrl && !editPreviewImage) {
			URL.revokeObjectURL(previewUrl);
		}
		setFileUploadError('');
		const isImage = file.type.startsWith('image/') && file.type !== 'image/gif';
		if (!isImage) {
			message.error('Supported image formats: PNG, JPG, JPEG');
			return false;
		}

		const isSVG = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
		if (isSVG) {
			message.error('Supported image formats: PNG, JPG, JPEG');
			return false;
		}
		const fileSizeInMB = file.size / (1024 * 1024); // Binary MB (MiB)
		const fileSizeInDecimalMB = file.size / (1000 * 1000); // Decimal MB (what macOS shows)
		console.log(`File size - Binary: ${fileSizeInMB.toFixed(2)} MiB, Decimal: ${fileSizeInDecimalMB.toFixed(2)} MB`);
		const isLt10M = fileSizeInDecimalMB < 10; // Using binary for consistency with the 5MB limit
		if (!isLt10M) {
			message.error(`Image size must be less than 10MB! Current size: ${formatFileSize(file.size)}`);
			return false;
		}
		setSelectedFile(file);
		setFileName(file.name);

		const url = URL.createObjectURL(file);
		setPreviewUrl(url);
		setHasUploadedFile(true);

		// 编辑模式下检查表单变化
		if (mode === 'edit') {
			const currentValues = form.getFieldsValue();
			const hasChanges = checkFormChanges(currentValues);
			setHasFormChanged(hasChanges);
		}

		return false;
	};

	const handleUploadProgress = (event) => {};

	const handleSubmit = async (action = 'close') => {
		if (mode !== 'edit' && (!hasUploadedFile || !selectedFile)) {
			setFileUploadError('Image cannot be null');
			form.validateFields();
			return;
		}
		form
			.validateFields()
			.then(async (values) => {
				if (mode !== 'edit' && !selectedFile) {
					setFileUploadError('Image cannot be null');
					return;
				}
				if (mode === 'edit') {
					const hasFormChanged = checkFormChanges(values);
					if (!hasFormChanged) {
						message.info('No changes detected');
						return;
					}
				}
				setFileUploadError('');
				try {
					if (mode === 'edit') {
						const formData = new FormData();
						if (selectedFile) {
							formData.append('file', selectedFile);
						}
						formData.append('issueType', selectedIssueTypes);
						formData.append('scene', values.scene);
						formData.append('position', values.position);
						formData.append('description', values.issueReason || '');
						// formData.append('baseImageId', editRecordId);

						await updateImage(editRecordId, formData);
						message.success('Image updated successfully!');
					} else {
						// Add mode: use uploadImageAndSubmit API
						const formData = new FormData();
						if (selectedFile) {
							formData.append('file', selectedFile);
						}
						formData.append('issueType', selectedIssueTypes);
						formData.append('scene', values.scene);
						formData.append('position', values.position);
						formData.append('description', values.issueReason || '');
						await uploadImageAndSubmit(formData, null, appId);
					}

					// Notify parent that upload was successful
					if (onUploadStatusChange) {
						onUploadStatusChange(true);
					}
					if (action === 'next' && mode !== 'edit') {
						clearAllData();
						form.resetFields();
						setSelectedIssueTypes([]);
						message.success('Image uploaded successfully');
					} else {
						onCancel();
						clearAllData();
					}
				} catch (error) {
					console.error('Upload failed:', error);
				}
			})
			.catch((info) => {
				console.error('Validation failed:', info);
			});
	};

	const handleConfirmApprove = async () => {
		try {
			const approveData = { ossKey: editOssKey };
			await approveImage(approveData, appId);
			onCancel();
			if (onUploadStatusChange) {
				onUploadStatusChange(true);
			}
			message.success('Item approved successfully');
		} catch (error) {
			if (error.response) {
				console.error('Approval failed:', error.response.data);
			}
		}
	};

	const uploadProps = {
		name: 'image',
		action: '#',
		headers: {
			'content-type': 'multipart/form-data',
		},
		accept: 'image/png,image/jpeg,image/jpg',
		showUploadList: false,
		beforeUpload: handleFileSelect,
		onProgress: handleUploadProgress,
		onChange: (info) => {
			if (info.file.status === 'done') {
			} else if (info.file.status === 'error') {
				console.log('Upload failed:', info.file);
			}
		},
		multiple: false,
		disabled: mode === 'approve',
	};

	const cantSubmit = mode === 'edit' ? !hasFormChanged : false;
	return (
		<Modal
			title={mode === 'edit' ? 'Edit Image' : mode === 'approve' ? 'Approve Image' : 'Add New'}
			open={visible}
			onCancel={() => {
				clearAllData();
				onCancel();
			}}
			className="upload-modal-image"
			footer={null}
			width={960}
			destroyOnHidden
			closeIcon={<img src={require('assets/images/closeBtn.png')} className="app-modal-close-btn" />}
		>
			<div className="modal-content-image">
				<div className="image-upload-section">
					<div className="upload-section">
						<Upload {...uploadProps}>
							<div
								className={fileUploadError ? 'upload-area-error' : 'upload-area'}
								style={{ cursor: mode === 'approve' ? 'not-allowed' : 'pointer' }}
							>
								{mode === 'edit' || mode === 'approve' ? (
									<div className="file-preview-full">
										{!previewUrl ? (
											<div className="image-loading-overlay">
												<img src={loadingUnscreenIcon} alt="Loading" className="image-loading-gif" />
												<p className="image-loading-text">Loading image...</p>
											</div>
										) : (
											<>
												<img src={previewUrl} alt="Preview" className="preview-image-full" />
												<div className="file-name-overlay">ID : {fileName}</div>
											</>
										)}
									</div>
								) : (
									<div className="file-preview-full">
										{!previewUrl ? (
											<div className="upload-area-image">
												<img src={uploadedIcon} alt="Upload" className="upload-icon" />
												<p className="upload-tips">Drop an image here or</p>
												<div className="upload-link">select an image</div>
												<div className="upload-support">Support: png, jpg, jpeg</div>
												<div className="upload-support-max">Maximum file size: 10MB</div>
											</div>
										) : (
											<>
												<img src={previewUrl} alt="Preview" className="preview-image-full" />
												<div className="file-name-overlay">ID : {fileName}</div>
											</>
										)}
									</div>
								)}
							</div>
						</Upload>
						{fileUploadError && <div className="file-upload-error">Image cannot be null / Maximum file size: 10MB</div>}
					</div>

					<div className="form-section">
						<Form
							form={form}
							layout="vertical"
							disabled={mode === 'approve'}
							initialValues={{
								scene: '',
								position: '',
								issueReason: '',
							}}
						>
							<Form.Item
								name="issueType"
								label="Issue Type"
								rules={[{ required: true, message: 'Issue Type cannot be null' }]}
							>
								<Select onChange={handleIssueTypeChange} className="issue-type-select" style={{ width: '100%' }}>
									{issueTypeOptions.map((option) => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</Select>
							</Form.Item>

							<div className="form-row">
								<Form.Item
									name="scene"
									label="Issue Scenario"
									rules={[{ required: true, message: 'Issue Scenario cannot be null' }]}
									className="form-item-half"
								>
									<Select
										onChange={() => {
											if (mode === 'edit') {
												const currentValues = form.getFieldsValue();
												const hasChanges = checkFormChanges(currentValues);
												setHasFormChanged(hasChanges);
											}
										}}
									>
										{issueScenarioOptions.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Form.Item>

								<Form.Item
									name="position"
									label="Issue Position"
									rules={[{ required: true, message: 'Issue Position cannot be null' }]}
									className="form-item-half"
								>
									<Select
										onChange={() => {
											if (mode === 'edit') {
												const currentValues = form.getFieldsValue();
												const hasChanges = checkFormChanges(currentValues);
												setHasFormChanged(hasChanges);
											}
										}}
									>
										{issuePositionOptions.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Form.Item>
							</div>

							<Form.Item
								name="issueReason"
								label="Issue Reason"
								rules={[
									{ required: true, message: 'Issue Reason cannot be null' },
									{ max: 500, message: 'Max 500 characters.' },
								]}
							>
								<Input.TextArea
									placeholder="Max 500 characters."
									style={{ minHeight: '158px' }}
									rows={4}
									onChange={() => {
										if (mode === 'edit') {
											const currentValues = form.getFieldsValue();
											const hasChanges = checkFormChanges(currentValues);
											setHasFormChanged(hasChanges);
										}
									}}
								/>
							</Form.Item>
						</Form>
					</div>
				</div>

				<div className="modal-footer">
					<Space>
						<Button
							onClick={() => {
								clearAllData();
								onCancel();
							}}
						>
							Cancel
						</Button>
						{mode !== 'edit' && mode !== 'approve' && (
							<Button onClick={() => handleSubmit('close')} color="purple" variant="outlined">
								Save and Close
							</Button>
						)}
						{mode !== 'edit' && mode !== 'approve' && (
							<Button onClick={() => handleSubmit('next')} color="purple" variant="solid">
								Save and Next
							</Button>
						)}
						{(mode === 'edit' || mode === 'approve') && (
							<Button
								onClick={() => {
									mode === 'edit' ? handleSubmit('next') : handleConfirmApprove();
								}}
								className={cantSubmit ? 'add-btn-disable-image' : 'add-btn'}
								color="purple"
								variant="solid"
								disabled={mode === 'edit' ? !hasFormChanged : false}
							>
								{mode === 'edit' ? 'Save' : 'Approve'}
							</Button>
						)}
					</Space>
				</div>
			</div>
		</Modal>
	);
};

export default AddImageModal;
